/*
  Home: the portal's parent dashboard, trimmed for a phone. Welcome line, the children, the next
  lesson as the portal's purple UpcomingLessonCard, today's lessons, and a reminder nudge.
*/
import { BellOutlined, RightOutlined } from "@ant-design/icons";
import UpcomingLessonCard from "app/components/Cards/UpcomingLessonCard";
import ConsentBanner from "app/components/ConsentBanner";
import Avatar from "app/components/Icons/Avatar";
import LessonRow from "app/components/LessonRow";
import LessonSheet, { studentNamesFor } from "app/components/LessonSheet";
import { TopBar } from "app/components/Shell";
import Spinner, { EmptyState } from "app/components/Spinner";
import { Button } from "app/components/v1/Button";
import { isNativeApp } from "app/native/nativeApp";
import { pushSupport } from "app/native/push";
import { useMeQuery } from "app/services/account";
import { useGetTimetableQuery } from "app/services/timetable";
import { useGetAnnouncementsQuery } from "app/services/notifications";
import type { Lesson } from "app/services/types";
import { MAIN_SELECTORS } from "app/store";
import storage, { STORAGE_KEYS } from "app/utils/storage";
import { isoDay, lessonPhase } from "app/utils/time";
import moment from "moment";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

export default function Home() {
  const { profile } = useSelector(MAIN_SELECTORS.user);
  const prefs = useSelector(MAIN_SELECTORS.reminders);
  const { data: me } = useMeQuery();
  const { data, isLoading, isError, refetch } = useGetTimetableQuery({ daysBack: 0, daysAhead: 14 }, { pollingInterval: 5 * 60 * 1000 });
  const [open, setOpen] = useState<Lesson | null>(null);
  const { data: announcements } = useGetAnnouncementsQuery(undefined, { pollingInterval: 5 * 60 * 1000 });
  const latest = announcements?.items.find((a) => !a.read) ?? null;

  const children = data?.children ?? me?.children ?? [];
  const today = moment().format("YYYY-MM-DD");
  const todays = useMemo(() => (data?.lessons ?? []).filter((l) => isoDay(l.start) === today), [data, today]);
  const next = useMemo(() => (data?.lessons ?? []).find((l) => !l.cancelled && lessonPhase(l.start, l.end) !== "past") ?? null, [data]);
  const permissionsAsked = Boolean(storage.getItem(STORAGE_KEYS.PHONE_ASKED));
  const showReminderNudge = !permissionsAsked && (isNativeApp() || pushSupport() === "ready" || pushSupport() === "install-first");

  return (
    <div className="ct-page">
      <TopBar right={<Link to="/settings" aria-label="Settings"><Avatar name={profile?.avatar} fullName={`${profile?.name ?? ""} ${profile?.lastName ?? ""}`} size={36} /></Link>} />
      <h1 style={{ fontSize: 26 }}>Welcome{profile?.name ? `, ${profile.name}` : ""}</h1>
      {children.length > 0 && (
        <div className="d-flex gap-2 flex-wrap mb-3">
          {children.map((c) => (
            <span key={c.id} className="d-inline-flex align-items-center gap-2 bg-white border rounded-pill ps-1 pe-3 py-1" style={{ borderColor: "#e2e2ea", fontWeight: 600, color: "#171725" }}>
              <Avatar name={c.avatar} fullName={`${c.name} ${c.lastName}`} size={28} /> {c.name}{c.year ? <span className="ct-muted" style={{ fontWeight: 500 }}>· {c.year}</span> : null}
            </span>
          ))}
        </div>
      )}

      <ConsentBanner />

      {latest && (
        <Link to="/messages" className="ct-card p-3 mb-3 d-block" style={{ color: "inherit", borderLeft: "4px solid #4c54a0" }}>
          <div className="ct-subtitle" style={{ color: "#4c54a0" }}>Announcement{announcements && announcements.unread > 1 ? ` · ${announcements.unread} new` : ""}</div>
          <div style={{ fontWeight: 700, color: "#171725" }}>{latest.title || "From the ClassTutor team"}</div>
          <div className="ct-muted" style={{ fontSize: 13, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{latest.body}</div>
        </Link>
      )}

      {showReminderNudge && (
        <Link to="/settings" className="ct-card p-3 mb-3 d-flex align-items-center gap-3" style={{ color: "inherit" }}>
          <span style={{ width: 40, height: 40, borderRadius: 12, background: "#eceefa", color: "#4c54a0", display: "grid", placeItems: "center", fontSize: 20 }}><BellOutlined /></span>
          <span className="flex-grow-1">
            <span style={{ fontWeight: 700, color: "#171725", display: "block" }}>Turn on lesson alarms</span>
            <span className="ct-muted" style={{ fontSize: 13 }}>Get a loud reminder {prefs.minutesBefore} minutes before each lesson.</span>
          </span>
          <RightOutlined className="ct-muted" />
        </Link>
      )}

      {isLoading && !data ? <Spinner tip="Loading your lessons" /> : isError && !data ? (
        <div className="ct-card p-4 text-center">
          <div style={{ fontWeight: 700 }}>We could not load your lessons.</div>
          <Button className="mt-3" buttonType="outlined" name="Try again" onClick={() => refetch()} />
        </div>
      ) : (
        <>
          {next ? (
            <section className="mb-4">
              <UpcomingLessonCard lesson={next} studentNames={studentNamesFor(next, children)} label={isoDay(next.start) === today ? "Next Lesson" : "Upcoming Lesson"} />
            </section>
          ) : (
            <section className="mb-4"><EmptyState title="No upcoming lessons" hint="Lessons appear here as soon as they are on the timetable." /></section>
          )}

          <section className="mb-4">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h2 className="ct-section-title m-0" style={{ fontSize: 16 }}>Today</h2>
              <Link to="/timetable" style={{ fontWeight: 600, fontSize: 14 }}>Full timetable <RightOutlined style={{ fontSize: 11 }} /></Link>
            </div>
            {todays.length ? todays.map((l) => <LessonRow key={l.registerId} lesson={l} studentNames={studentNamesFor(l, children)} onClick={() => setOpen(l)} />)
              : <div className="ct-card p-3 ct-muted text-center">No lessons today.</div>}
          </section>
        </>
      )}

      <section className="mb-3">
        <Link to="/messages" className="ct-card p-3 d-flex align-items-center justify-content-between" style={{ color: "inherit" }}>
          <span><span style={{ fontWeight: 700, color: "#171725", display: "block" }}>Message the ClassTutor team</span><span className="ct-muted" style={{ fontSize: 13 }}>Questions about lessons, payments or anything else.</span></span>
          <RightOutlined className="ct-muted" />
        </Link>
      </section>

      <LessonSheet lesson={open} children={children} prefs={prefs} onClose={() => setOpen(null)} />
    </div>
  );
}
